/**
 * Charge visibility now lives on the per-window join edge
 * (`EventSignUpWindow.charges` -> edge `memberControl`) rather than on the
 * `EventRegistrationChargeType` node itself.
 *
 * A patron can be eligible for more than one sign-up window, and the same
 * charge can appear on several of those windows with different `memberControl`
 * values. For the patron-facing surfaces we merge the charges across every
 * eligible window, de-duped by charge id, keeping the most permissive
 * visibility (SELECTABLE > VISIBLE > HIDDEN).
 *
 * Note: for patron callers the backend already strips `HIDDEN` (and therefore
 * staff-only / admin-only) charges from `window.charges`, so in practice only
 * VISIBLE / SELECTABLE edges reach this helper. HIDDEN is handled defensively.
 */

export type MergeableMemberControl = "HIDDEN" | "VISIBLE" | "SELECTABLE";
export type MergeableStaffControl = "SELECTABLE" | "ADMIN_ONLY";

// Control fields are typed loosely as `string` so the relay-generated enum
// unions (which include `"%future added value"`) are assignable and don't
// disrupt inference of TNode. Unknown values normalize to the least permissive.
type Maybe<T> = T | null | undefined;

type ChargeEdgeLike<TNode> = Maybe<{
  readonly memberControl: string;
  // staffControl is only selected on staff-facing fragments; patron fragments
  // omit it, so it may be absent.
  readonly staffControl?: string | null;
  readonly node: Maybe<TNode>;
}>;

type EligibleWindowLike<TNode> = Maybe<{
  readonly charges: Maybe<{
    readonly edges: Maybe<ReadonlyArray<ChargeEdgeLike<TNode>>>;
  }>;
}>;

export type MergedWindowCharge<TNode> = {
  node: TNode;
  memberControl: MergeableMemberControl;
  staffControl: MergeableStaffControl | null;
};

const MEMBER_CONTROL_RANK: Record<MergeableMemberControl, number> = {
  HIDDEN: 0,
  VISIBLE: 1,
  SELECTABLE: 2,
};

// More permissive = accessible to more staff: SELECTABLE (any staff) beats
// ADMIN_ONLY (department admins only).
const STAFF_CONTROL_RANK: Record<MergeableStaffControl, number> = {
  ADMIN_ONLY: 0,
  SELECTABLE: 1,
};

const normalizeMemberControl = (value: string): MergeableMemberControl =>
  value === "VISIBLE" || value === "SELECTABLE" ? value : "HIDDEN";

const normalizeStaffControl = (
  value: string | null | undefined
): MergeableStaffControl | null => {
  if (value === "SELECTABLE" || value === "ADMIN_ONLY") {
    return value;
  }
  return null;
};

const morePermissiveStaffControl = (
  a: MergeableStaffControl | null,
  b: MergeableStaffControl | null
): MergeableStaffControl | null => {
  if (a == null) return b;
  if (b == null) return a;
  return STAFF_CONTROL_RANK[a] >= STAFF_CONTROL_RANK[b] ? a : b;
};

/**
 * Flatten the charges on a set of sign-up windows into a single list, de-duped
 * by charge node id, keeping the most permissive control seen for each charge
 * (memberControl: SELECTABLE > VISIBLE > HIDDEN; staffControl: SELECTABLE >
 * ADMIN_ONLY). Order follows first appearance across the windows.
 *
 * Works for both the patron-facing `clientEligibleSignUpWindows` (member
 * visibility) and the staff sign-up `signUpWindows` (which also carry
 * staffControl on the edge).
 */
export const mergeEligibleWindowCharges = <
  TNode extends { readonly id: string }
>(
  eligibleWindows: Maybe<ReadonlyArray<EligibleWindowLike<TNode>>>
): MergedWindowCharge<TNode>[] => {
  const byId = new Map<string, MergedWindowCharge<TNode>>();

  for (const window of eligibleWindows ?? []) {
    for (const edge of window?.charges?.edges ?? []) {
      const node = edge?.node;
      if (!node) {
        continue;
      }

      const memberControl = normalizeMemberControl(edge.memberControl);
      const staffControl = normalizeStaffControl(edge.staffControl);

      const existing = byId.get(node.id);
      if (!existing) {
        byId.set(node.id, { node, memberControl, staffControl });
        continue;
      }

      if (
        MEMBER_CONTROL_RANK[memberControl] >
        MEMBER_CONTROL_RANK[existing.memberControl]
      ) {
        existing.memberControl = memberControl;
        existing.node = node;
      }
      existing.staffControl = morePermissiveStaffControl(
        existing.staffControl,
        staffControl
      );
    }
  }

  return Array.from(byId.values());
};
